import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { INoDataResponse } from '../../../../core/interfaces/INoDataResponse';
import { ApiService } from '../../../../core/services/ApiService';
import { TerminalParameterKeys } from '../../../terminals/core/enums/TerminalParameterKeys';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { WorkerRequirementService } from './WorkerRequirementService';

@Injectable({
  providedIn: 'root'
})
export class WorkerService {

  private readonly _basePath = '/workers';
  private readonly _apiService = inject(ApiService);
  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _workerRequirement = inject(WorkerRequirementService);

  public openSafe(): Observable<void> {
    return this.post(`${this._basePath}/open-safe`);
  }

  public printLastReceipt(terminalSessionId: number): Observable<void> {
    return this.post(
      `${this._basePath}/print-last-receipt`,
      {
        terminalSessionId,
        printerName: this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.CSH_BONPRINTER_PORT_CONFIG]
      }
    );
  }

  public printTicket(ticketNumber: number): Observable<void> {
    return this.post(
      `${this._basePath}/print-ticket`,
      {
        ticketNumber,
        printerName: this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.CSH_TICKETPRINTER_PORT_CONFIG]
      }
    );
  }

  public printVoucher(voucherId: number): Observable<void> {
    return this._apiService.post(
      `${this._basePath}/print-voucher`,
      {
        voucherId,
        printerName: this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.CSH_TICKETPRINTER_PORT_CONFIG]
      }
    );
  }

  public printCard(cardId: number): Observable<void> {
    return this._apiService.post(
      `${this._basePath}/print-card`,
      {
        cardId,
        printerName: this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.CSH_TICKETPRINTER_PORT_CONFIG]
      }
    );
  }

  public printReceipt(transactionId: number): Observable<void> {
    return this.post(
      `${this._basePath}/print-receipt`,
      {
        transactionId
      }
    );
  }

  public printKitchen(transactionId: number): Observable<void> {
    return this._apiService.post(
      `${this._basePath}/print-kitchen`,
      {
        transactionId
      }
    );
  }

  public printEcBufferedReceipt(printCustomerCopy: boolean): Observable<void> {
    return this.post(
      `${this._basePath}/print-ec-buffered-receipt`,
      {
        printerName: this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.CSH_BONPRINTER_PORT_CONFIG],
        printCustomerCopy
      }
    );
  }

  public printTransaction(transactionId: number, printReservation: boolean = false): Observable<void> {
    return this.post(
      `${this._basePath}/print-transaction`,
      {
        transactionId,
        printReservation
      }
    );
  }

  public printDocument(documentContent: string): Observable<void> {
    return this.post(
      `${this._basePath}/print-document`,
      {
        documentContent,
        printerName: this._terminalsStore.terminalParametersMap()[TerminalParameterKeys.WEB_CASH_DOCUMENTS_PRINTER_NAME]
      }
    );
  }

  public openCustomerScreen(url: string): Observable<void> {
    return this.post(
      `${this._basePath}/open-customer-screen`,
      { url, userAgent: navigator.userAgent }
    );
  }

  public closeCustomerScreen(): Observable<void> {
    return this.post(`${this._basePath}/close-customer-screen`);
  }

  public reconnectTerminal(terminalId: number, terminalSessionId: number): Observable<void> {
    return this.post(`${this._basePath}/reconnect-terminal`, { terminalId, terminalSessionId });
  }

  public endOfDay(): Observable<void> {
    return this.post(`${this._basePath}/end-of-day`);
  }

  public notifyTerminalSessionOpen(terminalId: number, terminalSessionId: number): Observable<INoDataResponse> {
    return this.post(`${this._basePath}/notify-terminal-session-open`, {
      terminalId,
      terminalSessionId
    });
  }

  public notifyTerminalSessionEnd(terminalSessionId: number): Observable<INoDataResponse> {
    return this.post(`${this._basePath}/notify-terminal-session-end`, {
      terminalSessionId
    });
  }

  private post<T>(path: string, body: object = {}): Observable<T> {
    if (!this._workerRequirement.isWorkerRequired()) {
      return of(undefined as T);
    }

    return this._apiService.post(path, body);
  }
}
